import React, { useState } from "react";

import {
  findDriverCodeClash,
  normalizeDriverLastName,
  performUpdateUserReloadCarrierMini,
  sanitizeCarrierUser,
} from "../../../graphql/carrier/carrierCRUD";

import { SaveButton, useNotify, useRedirect, useRefresh } from "react-admin";
import { useForm } from "react-final-form";

import { logError } from "../../../utils/appUtils";
import {
  administeredCarrierJunctions,
  carrierYardName,
} from "../../../utils/carrierUserUtils";

export const ValidateSaveButton = ({
  record,
  appUser,
  isSelf,
  backPath,
  ...props
}) => {
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const [isSaving, setIsSaving] = useState(false);

  const handleValidateSave = async () => {
    const formData = form.getState().values;
    const lastNameChanged =
      normalizeDriverLastName(formData.lastName) !==
      normalizeDriverLastName(record?.lastName);
    if (record?.driverCode && lastNameChanged) {
      setIsSaving(true);
      try {
        const clash = await findDriverCodeClash(record, formData.lastName);
        if (clash) {
          notify(
            `Another driver (${clash.firstName || ""} ${clash.lastName || ""}`.trim() +
              `) already uses code ${record.driverCode} with this last name. ` +
              "Saving would lock both drivers out of code lookup at the gate.",
            "error",
          );
          setIsSaving(false);
          return; // halt submission
        }
      } catch (error) {
        // Fail open: a transient query error shouldn't block the edit.
        logError(
          "carrierApp:carrierUsers:ValidateSaveButton:findDriverCodeClash",
          error,
          false,
        );
      }
      setIsSaving(false);
    }
    props.handleSubmitWithRedirect();
  };

  const transform = (data) => {
    data = { ...data };

    // Gate-enrolled drivers have no email.
    if (data.email) data.email = data.email.toLowerCase();

    return sanitizeCarrierUser(data);
  };

  const onSuccess = async ({ data }) => {
    try {
      const junctions = data.reloadYardCarriers?.items;
      const actorJunctions = appUser?.reloadYardCarriers?.items;

      // Names and contact details only. Which transloads a carrier user works
      // is not edited here: joining the company grants all of them, and taking
      // one away is that transload's own decision (its manager blocks or
      // removes the user there). Leaving the company altogether is the delete
      // buttons beside this one.
      //
      // Every junction keeps its own copy of the details — the lists and the
      // yards' own screens read them from there rather than from the user record
      // — so an edit has to reach each row it is allowed to touch.
      const userFields = {
        carrierUserId: data.id,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
      };

      const detailsChanged = ["firstName", "lastName", "phone"].some(
        (field) => data[field] !== record?.[field],
      );

      // For a colleague the sync stops at the rows the actor administers: a
      // membership at a yard the actor has no standing in is not theirs to
      // touch, so that row keeps the old name where the lists and search read
      // it — staleness that needs a backend fan-out off the CarrierUser
      // record, not a client loop. A person's own rows are a different matter:
      // every one of them is owner-writable and every one of them is their
      // name, so a self-edit reaches them all — a blocked row included, or the
      // yard that blocked them would file them under a stale name.
      const detailTargets = isSelf
        ? (junctions ?? []).filter((junction) => junction?.active === "true")
        : administeredCarrierJunctions(junctions, actorJunctions);

      const operations = detailsChanged
        ? detailTargets.map((junction) => ({
            reloadYardId: junction.reloadYardId,
            run: () =>
              performUpdateUserReloadCarrierMini({ ...junction, ...userFields }),
          }))
        : [];

      // Each row is its own write and its own outcome. One failure must not
      // hide what the other rows already committed behind a single thrown
      // error: the user is told which transloads still need saving, and trying
      // again is safe — the rows that landed are simply written the same way
      // twice.
      const responses = await Promise.allSettled(
        operations.map((operation) => operation.run()),
      );
      const failed = operations.filter(
        (_, index) => responses[index].status === "rejected",
      );

      if (failed.length) {
        responses
          .filter((response) => response.status === "rejected")
          .forEach((response) =>
            logError(
              "carrierApp:carrierUsers:ValidateSaveButton:onSuccess",
              response.reason,
              false,
            ),
          );
        const failedNames =
          failed
            .map((operation) =>
              carrierYardName(operation.reloadYardId, actorJunctions),
            )
            .filter(Boolean)
            .join(", ") || "some transloads";
        // Refreshed rather than redirected: the form redraws from the record and
        // the junctions as they now stand, so what it shows is what a retry
        // would be writing.
        notify(
          `Some changes did not save — ${failedNames}. Please try again.`,
          "warning",
        );
        refresh();
        return;
      }

      notify(`User updated successfully.`);
      redirect(backPath);
    } catch (error) {
      logError(
        "carrierApp:carrierUsers:ValidateSaveButton:onSuccess",
        error,
        notify,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError(
      "carrierApp:carrierUsers:ValidateSaveButton:onFailure",
      error,
      notify,
    );
  };

  return (
    <SaveButton
      {...props}
      submitOnEnter={false}
      undoable={false}
      disabled={props.pristine}
      saving={isSaving}
      handleSubmitWithRedirect={handleValidateSave}
      transform={transform}
      onSuccess={onSuccess}
      onFailure={onFailure}
    />
  );
};
